import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';
import { productCategories } from '@/mocks/products';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const productsTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { totalItems } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleProductsEnter = () => {
    if (productsTimeout.current) clearTimeout(productsTimeout.current);
    setProductsOpen(true);
  };

  const handleProductsLeave = () => {
    productsTimeout.current = setTimeout(() => setProductsOpen(false), 200);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center shrink-0">
              <img
                src={scrolled
                  ? "https://storage.helloreaddy.io/project_files/23c94d18-5867-4539-9c50-49f9128bb6d3/14d8b0d0-06a0-4062-a34a-10ed04e0ef31_compressed_Logo-vecto-schw.webp"
                  : "https://storage.helloreaddy.io/project_files/23c94d18-5867-4539-9c50-49f9128bb6d3/7ea93e17-4f2d-488e-93b2-5ef0dcb47ee1_compressed_Qoqo-wei.webp"
                }
                alt="QOQO Cosmetics"
                className="h-7 md:h-9 w-auto object-contain"
                width={120}
                height={40}
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-8">
              <div
                className="relative"
                onMouseEnter={handleProductsEnter}
                onMouseLeave={handleProductsLeave}
              >
                <button className="flex items-center gap-1 text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors whitespace-nowrap">
                  Produkte
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`ri-arrow-down-s-line transition-transform ${productsOpen ? 'rotate-180' : ''}`} />
                  </span>
                </button>
                {productsOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg shadow-lg border border-background-200/60 py-2 overflow-hidden">
                    {productCategories.map((cat) =>
                      cat.comingSoon ? (
                        <div
                          key={cat.id}
                          className="flex items-center justify-between px-4 py-2.5 text-sm text-foreground-400 cursor-default"
                        >
                          <span>{cat.name}</span>
                          <span className="ml-3 px-2 py-0.5 rounded-full bg-background-200 text-[10px] font-label uppercase tracking-wide text-foreground-500 whitespace-nowrap">
                            Coming soon
                          </span>
                        </div>
                      ) : (
                        <Link
                          key={cat.id}
                          to={`/produkte/${cat.slug}`}
                          className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors"
                          onClick={() => setProductsOpen(false)}
                        >
                          {cat.name}
                        </Link>
                      )
                    )}
                    <div className="border-t border-background-200/60 mt-1 pt-1">
                      <Link
                        to="/produkte"
                        className="block px-4 py-2.5 text-sm font-medium text-primary-600 hover:bg-background-100 transition-colors"
                        onClick={() => setProductsOpen(false)}
                      >
                        Alle Produkte
                      </Link>
                    </div>
                  </div>
                )}
              </div>
              <Link to="/qualitaet" className="text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors whitespace-nowrap">
                Qualität
              </Link>
              <Link to="/ueber-uns" className="text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors whitespace-nowrap">
                Über uns
              </Link>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-4">
              <Link
                to="/warenkorb"
                className="relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors"
                aria-label="Warenkorb"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shopping-bag-line text-lg text-foreground-800" />
                </span>
                {totalItems > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-primary-500 text-background-50 text-xs font-semibold rounded-full flex items-center justify-center">
                    {totalItems}
                  </span>
                )}
              </Link>
              <button
                className="lg:hidden w-10 h-10 flex items-center justify-center"
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="Menü"
              >
                <span className="w-6 h-6 flex items-center justify-center">
                  <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl text-foreground-800`} />
                </span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-background-50/98 backdrop-blur-md pt-20 px-6 pb-8 overflow-y-auto">
          <div className="flex flex-col gap-1">
            <div className="border-b border-background-200/60 pb-2 mb-2">
              <p className="text-xs uppercase tracking-widest text-foreground-500 font-label mb-2">Produkte</p>
              {productCategories.map((cat) =>
                cat.comingSoon ? (
                  <div
                    key={cat.id}
                    className="flex items-center justify-between py-2.5 text-base text-foreground-400"
                  >
                    <span>{cat.name}</span>
                    <span className="ml-3 px-2.5 py-1 rounded-full bg-background-200 text-[10px] font-label uppercase tracking-wide text-foreground-500 whitespace-nowrap">
                      Coming soon
                    </span>
                  </div>
                ) : (
                  <Link
                    key={cat.id}
                    to={`/produkte/${cat.slug}`}
                    className="block py-2.5 text-base text-foreground-700 hover:text-primary-600 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    {cat.name}
                  </Link>
                )
              )}
              <Link
                to="/produkte"
                className="block py-2.5 text-base font-medium text-primary-600"
                onClick={() => setMobileOpen(false)}
              >
                Alle Produkte
              </Link>
            </div>
            {[
              { to: '/qualitaet', label: 'Qualität' },
              { to: '/ueber-uns', label: 'Über uns' },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="block py-3 text-lg font-medium text-foreground-800 hover:text-primary-600 transition-colors border-b border-background-200/40"
                onClick={() => setMobileOpen(false)}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </>
  );
}